import React from 'react';
import { Card, Field, TextField, SaveButton, useSection, ColorInput } from '../AdminUI';
import { Switch } from '../../ui/switch';
import { Plus, Trash2, GripVertical } from 'lucide-react';

const EMPTY = { enabled: true, title: 'DEALING TERMS', accentColor: '#0ea560', rows: [] };

export default function DealingTermsSection() {
  const [raw, setLocal, save] = useSection('dealingTerms');
  const local = raw || EMPTY;
  const set = (k, v) => setLocal({ ...local, [k]: v });
  const setRow = (i, patch) => set('rows', local.rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => set('rows', [...(local.rows || []), { id: `dt_${Date.now()}`, label: 'Label', value: 'Value' }]);
  const rmRow = (i) => set('rows', local.rows.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const arr = [...local.rows];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set('rows', arr);
  };

  return (
    <div data-testid="dealing-terms-admin">
      <Card
        title="Section Visibility"
        desc="Turn the Dealing Terms section on/off on the site."
        footer={<SaveButton onClick={save} />}
      >
        <div className="flex items-center gap-3">
          <Switch checked={local.enabled !== false} onCheckedChange={(v) => set('enabled', v)} data-testid="dealing-terms-enabled-toggle" />
          <span className="text-sm text-zinc-400">{local.enabled !== false ? 'Section is visible' : 'Section is hidden'}</span>
        </div>
      </Card>

      <Card
        title="Dealing Terms"
        desc="Skewed rate/rules rows shown between Exchanges and Payments strip."
        footer={<SaveButton onClick={save} />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <Field label="Section Title">
            <TextField value={local.title || ''} onChange={(v) => set('title', v)} data-testid="dealing-terms-title" />
          </Field>
          <Field label="Row Color (RGB / Hex)" hint="Applied as a subtle gradient to every row.">
            <ColorInput value={local.accentColor || '#0ea560'} onChange={(v) => set('accentColor', v)} />
          </Field>
        </div>

        <div className="space-y-2.5">
          {(local.rows || []).map((r, i) => (
            <div key={r.id || i} className="grid grid-cols-1 md:grid-cols-[24px_1fr_1fr_44px_44px_44px] gap-2 items-center rounded-md border border-zinc-800 bg-zinc-950/60 p-2" data-testid={`dealing-term-row-${i}`}>
              <div className="text-zinc-600 hidden md:flex justify-center"><GripVertical size={14} /></div>
              <TextField value={r.label} onChange={(v) => setRow(i, { label: v })} placeholder="Label (e.g. Min Refill)" />
              <TextField value={r.value} onChange={(v) => setRow(i, { value: v })} placeholder="Value (e.g. 1 Lac Points)" />
              <button onClick={() => move(i, -1)} className="h-9 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white text-xs" title="Move up">↑</button>
              <button onClick={() => move(i, +1)} className="h-9 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white text-xs" title="Move down">↓</button>
              <button onClick={() => rmRow(i)} className="h-9 rounded-md bg-zinc-950 border border-zinc-800 text-red-400 hover:bg-red-600 hover:text-white flex items-center justify-center" title="Remove">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <button onClick={addRow} className="w-full h-10 rounded-md border border-dashed border-zinc-700 hover:border-[color:var(--gold)]/50 text-zinc-300 text-sm font-semibold flex items-center justify-center gap-1.5" data-testid="dealing-terms-add-row">
            <Plus size={14} /> Add Row
          </button>
        </div>
      </Card>
    </div>
  );
}
